import Grid from '@components/Grid'
import MailResponse from '@components/MailResponse'
import Alert from '@components/UI/Alert'
import Button from '@components/UI/Button'
import { FieldFeedback, FieldLabel } from '@components/UI/Field'
import Icon from '@components/UI/Icon'
import Input from '@components/UI/Input'
import Loading from '@components/UI/Loading'
import useClassName from '@hooks/useClassName'
import Heading from '@components/UI/Typography/Heading'
import Paragraph from '@components/UI/Typography/Paragraph'
import css from '@utils/css'

import { Navigation } from "@layouts/NavigationV2"
import { objectToFormData } from '@utils/form'
import RenderIf from '@utils/renderIf'

import React, { useRef, useState, useEffect } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'

const ContactUs = () => {
    const recaptchaRef = useRef(null)

    const [loading, setLoading] = useState(false)
    const [sent, setSent] = useState(false)
    const [response, setResponse] = useState({})
    const [errors, setErrors] = useState({})

    const [email, setEmail] = useState('');
    const [subject, setSubject] = useState('');

    useEffect(() => {
        const params = new URL(document.location).searchParams;

        //replace space with plus sign for email
        let email = params.get('email');
        if (email) {
            email.replace(/ /g, '+');
            setEmail(email);
        }

        let subject = params.get('subject');
        subject && setSubject(subject);

    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault()

        if (!loading) {
            setLoading(true)
            setSent(false)

            const formData = objectToFormData(event.target)
            formData.append('recaptcha', recaptchaRef.current.getValue())

            await fetch(`${ process.env.NEXT_PUBLIC_APP_URL }/api/v1/contact`, {
                method: 'POST',
                body: formData
            })
                .then(response => response.json())
                .then(data => {
                    if (data.result == 'success') {
                        setResponse(data.params)
                        setErrors({})
                        setSent(true)

                        setSubject('')
                        event.target.reset()
                    } else {
                        setErrors(data.data.errors)
                    }

                    setLoading(false)
                    recaptchaRef.current.reset()
                })
        }
    }

    return (
        <>
            <Navigation
                staticTheme="light"
                actionTheme="light"
                background="neutral-100"
            />
            <div className={ css(
                'bg-neutral-100 py-6',
                '480:(py-8 px-5)',
                '768:(py-10 px-8)',
                '1024:(py-12)',
                '1280:(py-14)'
            ) }
            >
                <div
                    className={ css(
                        'rounded-lg shadow-[0_4px_24px_0px_rgba(29,30,44,0.1)] bg-white max-w-[744px]',
                        'mx-auto py-8 px-5',
                        '480:(px-10)',
                        '768:(p-12)',
                        '1024:(px-16)'
                    ) }
                >
                    <h1
                        className='h2 768:desktop-heading-2 mb-2 768:mb-4 text-center'
                    >
                        Contact Us
                    </h1>

                    <p
                        className='mobile-body-2 768:desktop-body-2 text-center mb-8 768:mb-10'
                    >
                        Please fill out the form below and someone from Traqq will respond soon. Thank You!
                    </p>

                    { /* TODO: come up with component for the form */ }
                    <form
                        className='contact-us__form'
                        method='POST'
                        acceptCharset='UTF-8'
                        onSubmit={ handleSubmit }
                    >
                        <Grid
                            xs={ 1 }
                            md={ 2 }
                            g={ 6 }
                        >
                            <div>
                                <FieldLabel
                                    htmlFor='firstName'
                                >
                                    First Name*
                                </FieldLabel>

                                <Input
                                    name='firstName'
                                    type='text'
                                    // placeholder='John'
                                    id='firstName'
                                    required
                                    mods={ { invalid: Boolean(errors.firstName) } }
                                    inputProps={ { maxLength: 64 } }
                                />

                                <RenderIf
                                    isTrue={ errors.firstName }
                                >
                                    <FieldFeedback
                                        invalid
                                    >
                                        { errors.firstName?.join('\n') }
                                    </FieldFeedback>
                                </RenderIf>
                            </div>
                            <div>
                                <FieldLabel
                                    htmlFor='lastName'
                                >
                                    Last Name*
                                </FieldLabel>

                                <Input
                                    name='lastName'
                                    type='text'
                                    // placeholder='Doe'
                                    id='lastName'
                                    required
                                    mods={ { invalid: Boolean(errors.lastName) } }
                                    inputProps={ { maxLength: 64 } }
                                />

                                <RenderIf
                                    isTrue={ errors.lastName }
                                >
                                    <FieldFeedback
                                        invalid
                                    >
                                        { errors.lastName?.join('\n') }
                                    </FieldFeedback>
                                </RenderIf>
                            </div>

                            <div>
                                <FieldLabel
                                    htmlFor='email'
                                >
                                    Your email address*
                                </FieldLabel>

                                <Input
                                    name='email'
                                    type='email'
                                    // placeholder='email@domain.com'
                                    id='email'
                                    required
                                    mods={ { invalid: Boolean(errors.email) } }
                                    inputProps={ { maxLength: 64 } }
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                />

                                <RenderIf
                                    isTrue={ errors.email }
                                >
                                    <FieldFeedback
                                        invalid
                                    >
                                        { errors.email?.join('\n') }
                                    </FieldFeedback>
                                </RenderIf>
                            </div>
                            <div>
                                <FieldLabel
                                    htmlFor='subject'
                                >
                                    Subject*
                                </FieldLabel>

                                <Input
                                    name='subject'
                                    type='text'
                                    // placeholder='Enterprise plan'
                                    id='subject'
                                    required
                                    mods={ { invalid: Boolean(errors.subject) } }
                                    inputProps={ { maxLength: 64 } }
                                    value={subject}
                                    onChange={e => setSubject(e.target.value)}
                                />

                                <RenderIf
                                    isTrue={ errors.subject }
                                >
                                    <FieldFeedback
                                        invalid
                                    >
                                        { errors.subject?.join('\n') }
                                    </FieldFeedback>
                                </RenderIf>
                            </div>
                        </Grid>

                        <div className='mt-6'>
                            <FieldLabel
                                htmlFor='message'
                            >
                                Message*
                            </FieldLabel>

                            <Input
                                name='message'
                                type='textarea'
                                // placeholder='Type here...'
                                id='message'
                                rows={ 10 }
                                required
                                mods={ { invalid: Boolean(errors.message) } }
                                inputProps={ { maxLength: 2000 } }
                                className='!h-[200px]'
                            />

                            <RenderIf
                                isTrue={ errors.message }
                            >
                                <FieldFeedback
                                    invalid
                                >
                                    { errors.message?.join('\n') }
                                </FieldFeedback>
                            </RenderIf>
                        </div>

                        <div className={ css(
                            'mt-6 mb-8 flex flex-col gap-y-6 items-center justify-between',
                            '768:flex-row 768:mb-10'
                        ) }>
                            <ReCAPTCHA
                                className='!w-[280px]'
                                ref={ recaptchaRef }
                                sitekey={ process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY }
                            />

                            <RenderIf
                                isTrue={ errors.recaptcha }
                            >
                                <Alert
                                    severity='danger'
                                >
                                    { errors.recaptcha?.join('\n') }
                                </Alert>
                            </RenderIf>

                            <Button
                                type='submit'
                                variant='primary'
                                className='w-full button_md 768:w-[268px]'
                            >
                                { loading ? <Loading/> : 'Send message' }
                            </Button>
                        </div>
                    </form>

                    <div className='flex items-center justify-center'
                    >
                        <Icon
                            name='question-rounded'
                            color='violet-900'
                            size={ 16 }
                            className='!hidden 768:(!block) mr-2'
                        />
                        <p
                            className='desktop-body-3 text-violet-900 text-center'
                        >
                            Typical response time is within 48 hours on business days
                        </p>
                    </div>
                </div>

                <div
                    className='mx-5 mt-6 flex flex-col items-center gap-2 768:flex-row justify-center'
                >
                    <div className='flex justify-center items-center gap-x-2'>
                        <Icon
                            name='location-dot'
                            color={ ['neutral-900', 'violet-700'] }
                            size={ 24 }
                        />
                        <p className='desktop-subtitle'>
                            Sydney, Australia
                        </p>
                    </div>
                    <p
                        className='text-center desktop-body-3'
                    >
                        Level 10, 88 Phillip Street, Sydney NSW 2000
                    </p>
                </div>
            </div>

            <RenderIf
                isTrue={ sent }
            >
                <MailResponse
                    title={ response.title }
                    body={ response.body }
                />
            </RenderIf>
        </>
    )
}

export default ContactUs
